import { useEffect, useRef, useState } from 'react';
import type { CanvasSummary, VideoReference } from '@/api/types';
import { useTranslator } from '@/i18n/LocaleProvider';
import styles from './Overlays.module.css';

interface Props {
  videos: VideoReference[];
  canvases: CanvasSummary[];
  onSave: () => void;
  onAddToCanvas: (canvasId: number) => void;
  onNewCanvas: () => void;
  onClear: () => void;
}

/** Трей выбора внизу по центру: миниатюры, Save, «Add to canvas ▾», сброс (README «Selection tray»). */
export function SelectionTray({
  videos,
  canvases,
  onSave,
  onAddToCanvas,
  onNewCanvas,
  onClear,
}: Props) {
  const { t, tp } = useTranslator();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [menuOpen]);

  if (videos.length === 0) return null;
  const count = videos.length;

  return (
    <div className={styles.tray} role="toolbar" aria-label={t('catalog.selection')}>
      <div className={styles.thumbs} aria-hidden="true">
        {videos.slice(0, 4).map((video) => (
          <img
            key={video.id}
            src={video.extra?.thumbnail_url ?? ''}
            alt=""
            className={styles.thumb}
          />
        ))}
        {count > 4 ? <span className={styles.thumbMore}>+{count - 4}</span> : null}
      </div>
      <span className={styles.trayLabel}>{tp('catalog.videos', count)}</span>
      <div className={styles.trayDivider} aria-hidden="true" />
      <button type="button" className={styles.traySave} onClick={onSave}>
        <span className={styles.traySaveIcon} aria-hidden="true" />
        {t('catalog.save')}
      </button>
      <div ref={menuRef} className={styles.trayCanvasWrap}>
        <button
          type="button"
          className={styles.trayPrimary}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {t('catalog.addToCanvas')} ▾
        </button>
        {menuOpen ? (
          <div className={styles.trayMenu} role="menu">
            {canvases.map((canvas) => (
              <button
                key={canvas.id}
                type="button"
                role="menuitem"
                className={styles.trayMenuItem}
                onClick={() => {
                  setMenuOpen(false);
                  onAddToCanvas(canvas.id);
                }}
              >
                {canvas.cover_url ? (
                  <img src={canvas.cover_url} alt="" className={styles.trayMenuCover} />
                ) : (
                  <span className={styles.trayMenuCover} />
                )}
                <span className={styles.trayMenuName} data-clarity-mask="true">
                  {canvas.name}
                </span>
                <span className={styles.trayMenuMeta}>
                  {tp('catalog.itemsCount', canvas.nodes_count)}
                </span>
              </button>
            ))}
            <button
              type="button"
              role="menuitem"
              className={styles.trayMenuNew}
              onClick={() => {
                setMenuOpen(false);
                onNewCanvas();
              }}
            >
              {t('catalog.newCanvasEllipsis')}
            </button>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className={styles.trayClose}
        title={t('catalog.clearSelection')}
        aria-label={t('catalog.clearSelection')}
        onClick={onClear}
      >
        ×
      </button>
    </div>
  );
}
